import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { builderCertificatePath } from '../../components/course-builder/certificateLinks';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BookOpen, Clock, Users, Play, CheckCircle, Award,
  ChevronRight, GraduationCap, Code, Brain, Cloud, 
  Database, Sparkles, Layers, Lock, BarChart, Calendar
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const categoryIcons = {
  full_stack_development: Code,
  ai_machine_learning: Brain,
  generative_ai: Sparkles,
  cloud_devops: Cloud,
  data_science: Database,
  cybersecurity: Lock,
  mobile_development: Layers,
  other: BookOpen
};

const statusColors = {
  enrolled: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  dropped: 'bg-gray-100 text-gray-700'
};

const formatStatus = (status) => {
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// EnrollmentCard - moved outside main component
const EnrollmentCard = ({ enrollment, onContinue, onGenerateCertificate, onViewCertificate }) => {
  const course = enrollment.course;
  if (!course) return null;
  
  const CategoryIcon = categoryIcons[course.category] || BookOpen;
  
  return (
    <Card 
      className="group hover:shadow-lg transition-all duration-300 overflow-hidden"
      data-testid={`my-course-${course.id}`}
    >
      <div className="flex flex-col md:flex-row">
        {/* Thumbnail */}
        <div className="relative w-full md:w-48 h-40 md:h-auto overflow-hidden shrink-0">
          {course.thumbnail_url ? (
            <img 
              src={course.thumbnail_url} 
              alt={course.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <CategoryIcon className="h-12 w-12 opacity-50" />
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={statusColors[enrollment.status]}>
                  {enrollment.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                  {formatStatus(enrollment.status)}
                </Badge>
                {enrollment.enrolled_by_admin && (
                  <Badge variant="outline" className="text-xs">
                    Assigned by Admin
                  </Badge>
                )}
              </div>
              
              <h3 
                className="text-lg font-semibold hover:text-primary cursor-pointer transition-colors"
                onClick={() => onContinue(course.id)}
              >
                {course.title}
              </h3>
              
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {course.description}
              </p>
              
              <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {course.estimated_hours}h
                </span>
                <span className="flex items-center gap-1">
                  <BookOpen className="h-4 w-4" />
                  {enrollment.completed_lessons?.length || 0}/{course.lesson_count} lessons
                </span>
                {enrollment.started_at && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Started {formatDistanceToNow(new Date(enrollment.started_at), { addSuffix: true })}
                  </span>
                )}
              </div>
            </div>
            
            <div className="text-right shrink-0">
              <div className="text-2xl font-bold text-primary">
                {Math.round(enrollment.progress_percentage)}%
              </div>
              <div className="text-xs text-muted-foreground">Progress</div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <Progress value={enrollment.progress_percentage} className="h-2" />
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2 mt-4">
            {enrollment.status === 'completed' ? (
              <>
                {enrollment.certificate_id ? (
                  <Button 
                    variant="outline"
                    onClick={() => onViewCertificate(enrollment.certificate_id)}
                  >
                    <Award className="h-4 w-4 mr-2" />
                    View Certificate
                  </Button>
                ) : (
                  <Button onClick={() => onGenerateCertificate(course.id)}>
                    <Award className="h-4 w-4 mr-2" />
                    Get Certificate
                  </Button>
                )}
                <Button variant="outline" onClick={() => onContinue(course.id)}>
                  Review Course
                </Button>
              </>
            ) : (
              <Button onClick={() => onContinue(course.id)}>
                <Play className="h-4 w-4 mr-2" />
                {enrollment.progress_percentage > 0 ? 'Continue Learning' : 'Start Course'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

// CertificateCard - moved outside main component
const CertificateCard = ({ certificate, onView }) => (
  <Card className="hover:shadow-md transition-shadow">
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
          <Award className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <CardTitle className="text-base">{certificate.course_title}</CardTitle>
          <CardDescription className="text-xs">
            Issued {formatDistanceToNow(new Date(certificate.issue_date), { addSuffix: true })}
          </CardDescription>
        </div>
      </div>
    </CardHeader>
    <CardContent className="pb-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Certificate #</span>
        <span className="font-mono text-xs">{certificate.certificate_number}</span>
      </div>
      <div className="flex items-center justify-between text-sm mt-1">
        <span className="text-muted-foreground">Final Score</span>
        <span className="font-semibold">{certificate.final_score}%</span>
      </div>
    </CardContent>
    <CardFooter>
      <Button 
        variant="outline" 
        size="sm" 
        className="w-full"
        onClick={() => onView(certificate.id, certificate)}
        data-testid={`legacy-certificate-view-${certificate.id}`}
      >
        View Certificate
      </Button>
    </CardFooter>
  </Card>
);

const TechLearningMyCourses = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [enrollments, setEnrollments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [stats, setStats] = useState(null);

  const fetchMyCourses = useCallback(async (status = null) => {
    try {
      setLoading(true);
      let url = '/tech-learning/my-courses?per_page=50';
      if (status && status !== 'all') {
        url += `&status=${status}`;
      }
      const response = await api.get(url);
      setEnrollments(response.data.enrollments);
    } catch (error) {
      console.error('Failed to fetch my courses:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchCertificates = useCallback(async () => {
    try {
      const response = await api.get('/tech-learning/certificates');
      setCertificates(response.data.certificates);
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
    }
  }, [api]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/tech-learning/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchMyCourses(activeTab === 'all' ? null : activeTab);
    fetchCertificates();
    fetchStats();
  }, [fetchMyCourses, fetchCertificates, fetchStats, activeTab]);

  const handleContinueCourse = (courseId) => {
    navigate(`/tech-learning/course/${courseId}`);
  };

  const handleGenerateCertificate = async (courseId) => {
    try {
      await api.post(`/tech-learning/courses/${courseId}/certificate`);
      fetchCertificates();
    } catch (error) {
      console.error('Failed to generate certificate:', error);
    }
  };

  const handleViewCertificate = (certId, cert) => {
    navigate(builderCertificatePath(cert) || `/tech-learning/certificate/${certId}`);
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="tech-learning-my-courses">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <GraduationCap className="h-8 w-8 text-primary" />
              My Courses
            </h1>
            <p className="text-muted-foreground mt-1">
              Track your learning progress and achievements
            </p>
          </div>
          
          <Button onClick={() => navigate('/tech-learning')}>
            <BookOpen className="h-4 w-4 mr-2" />
            Browse Library
          </Button>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.total_enrolled}</div>
                  <div className="text-xs text-muted-foreground">Enrolled</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Play className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.in_progress}</div>
                  <div className="text-xs text-muted-foreground">In Progress</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.completed}</div>
                  <div className="text-xs text-muted-foreground">Completed</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Award className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.certificates_earned}</div>
                  <div className="text-xs text-muted-foreground">Certificates</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                  <BarChart className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.average_progress}%</div>
                  <div className="text-xs text-muted-foreground">Avg Progress</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All Courses</TabsTrigger>
            <TabsTrigger value="in_progress" data-testid="tab-progress">In Progress</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">Completed</TabsTrigger>
            <TabsTrigger value="certificates" data-testid="tab-certificates">Certificates</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4 mt-4">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <Card key={i} className="h-48 animate-pulse">
                  <div className="flex">
                    <div className="w-48 h-full bg-muted" />
                    <div className="flex-1 p-4">
                      <div className="h-4 bg-muted rounded w-24" />
                      <div className="h-6 bg-muted rounded w-3/4 mt-2" />
                      <div className="h-4 bg-muted rounded w-full mt-4" />
                    </div>
                  </div>
                </Card>
              ))
            ) : enrollments.length === 0 ? (
              <Card className="p-12 text-center">
                <BookOpen className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mt-4">No courses yet</h3>
                <p className="text-muted-foreground mt-2">
                  Start learning by enrolling in a course from the library
                </p>
                <Button className="mt-4" onClick={() => navigate('/tech-learning')}>
                  Browse Courses
                </Button>
              </Card>
            ) : (
              enrollments.map(enrollment => (
                <EnrollmentCard 
                  key={enrollment.id} 
                  enrollment={enrollment}
                  onContinue={handleContinueCourse}
                  onGenerateCertificate={handleGenerateCertificate}
                  onViewCertificate={handleViewCertificate}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="in_progress" className="space-y-4 mt-4">
            {enrollments.filter(e => e.status === 'in_progress').length === 0 ? (
              <Card className="p-8 text-center">
                <Play className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mt-4">No courses in progress</h3>
                <p className="text-muted-foreground mt-2">Start a course to see it here</p>
              </Card>
            ) : (
              enrollments.filter(e => e.status === 'in_progress').map(enrollment => (
                <EnrollmentCard 
                  key={enrollment.id} 
                  enrollment={enrollment}
                  onContinue={handleContinueCourse}
                  onGenerateCertificate={handleGenerateCertificate}
                  onViewCertificate={handleViewCertificate}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4 mt-4">
            {enrollments.filter(e => e.status === 'completed').length === 0 ? (
              <Card className="p-8 text-center">
                <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mt-4">No completed courses yet</h3>
                <p className="text-muted-foreground mt-2">Complete a course to earn a certificate</p>
              </Card>
            ) : (
              enrollments.filter(e => e.status === 'completed').map(enrollment => (
                <EnrollmentCard 
                  key={enrollment.id} 
                  enrollment={enrollment}
                  onContinue={handleContinueCourse}
                  onGenerateCertificate={handleGenerateCertificate}
                  onViewCertificate={handleViewCertificate}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="certificates" className="mt-4">
            {certificates.length === 0 ? (
              <Card className="p-8 text-center">
                <Award className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mt-4">No certificates yet</h3>
                <p className="text-muted-foreground mt-2">Complete courses to earn certificates</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {certificates.map(cert => (
                  <CertificateCard key={cert.id} certificate={cert} onView={handleViewCertificate} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default TechLearningMyCourses;
